import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { CalendarClock, RefreshCw, AlertCircle, AlertTriangle, Loader2, MapPin, X, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { environment } from "@/env/env.prod";
import { apiHeaders } from "@/Auth/api-headers.";
import { TeacherCombobox, type TeacherOption } from "./TeacherCombobox";

// ─────────────────────────────────────────────────────────────────────────
// Teaching Schedule — read-only. Backed by classrooms/getMyTimetable (own
// schedule) and classrooms/getFacultyTimetable (any colleague's, web-only —
// the backend independently verifies the target belongs to the caller's
// own institute before returning anything). There is no edit surface here
// — assigning/reassigning a subject teacher happens in the classroom-level
// Timetable editor (TimetableEditor.tsx).
//
// Rendered as a real <table> (hour rows × day columns) rather than an
// absolutely-positioned time canvas — a table row auto-grows to fit its
// tallest cell natively, so an expanded conflict block can never overlap
// or spill into a neighboring cell; everything below it in that row just
// shifts down, which is the one thing manual pixel-position math could
// never guarantee. periodLabel isn't guaranteed to be a parseable time
// range (a classroom's periods can be freely renamed to arbitrary text in
// TimetableEditor.tsx), so anything that doesn't parse renders in an
// "Unscheduled time" strip above the grid instead of being dropped.
//
// Double-booking is detected entirely client-side: if the schedule being
// viewed has two entries on the same day with overlapping times across two
// different classrooms, both are merged into one cell and highlighted —
// the data already contains both sides of the conflict, so no extra
// backend call is needed just to flag it here (contrast with the
// save-time check in TimetableEditor, which has to ask the backend
// because it's checking against classrooms the client can't otherwise
// see).
// ─────────────────────────────────────────────────────────────────────────

type MyTimetableEntry = {
    classRoomId: string;
    className: string;
    day: string;
    periodIndex: number;
    periodLabel: string;
    subject: string;
    subjectId: string | null;
    room: string;
};

const DAY_ORDER = [ "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday" ];
const DAY_SHORT: Record<string, string> = {
    Monday: "Mon", Tuesday: "Tue", Wednesday: "Wed", Thursday: "Thu",
    Friday: "Fri", Saturday: "Sat", Sunday: "Sun",
};

const DEFAULT_WINDOW_START_HOUR = 8;
const DEFAULT_WINDOW_END_HOUR = 18;
const HOUR_COLUMN_WIDTH = 76;
const DAY_COLUMN_WIDTH = 160;

// Stable per-classroom color so the same class looks the same every time
// it appears across the week — cycles through a fixed palette keyed by a
// simple hash of the classroom id.
const EVENT_COLORS = [
    { bg: "bg-blue-50", border: "border-blue-300", text: "text-blue-800", bar: "bg-blue-500" },
    { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-800", bar: "bg-emerald-500" },
    { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-800", bar: "bg-amber-500" },
    { bg: "bg-rose-50", border: "border-rose-300", text: "text-rose-800", bar: "bg-rose-500" },
    { bg: "bg-violet-50", border: "border-violet-300", text: "text-violet-800", bar: "bg-violet-500" },
    { bg: "bg-cyan-50", border: "border-cyan-300", text: "text-cyan-800", bar: "bg-cyan-500" },
    { bg: "bg-pink-50", border: "border-pink-300", text: "text-pink-800", bar: "bg-pink-500" },
];

function colorFor(classRoomId: string) {
    let hash = 0;
    for (let i = 0; i < classRoomId.length; i++) hash = (hash * 31 + classRoomId.charCodeAt(i)) >>> 0;
    return EVENT_COLORS[ hash % EVENT_COLORS.length ];
}

/// Parses "09:00-10:00" style labels into minutes-from-midnight. Handles
/// the common 12-hour-without-AM/PM default period naming (e.g.
/// "12:00-01:00" meaning noon-1pm) by rolling the end time forward 12h
/// when it would otherwise appear before the start. Returns null for any
/// label that isn't a recognizable time range (e.g. a renamed "Period 1").
function parsePeriodLabel(label: string): { start: number; end: number } | null {
    const m = label.match(/(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})/);
    if (!m) return null;
    const startH = parseInt(m[ 1 ], 10), startM = parseInt(m[ 2 ], 10);
    const endH = parseInt(m[ 3 ], 10), endM = parseInt(m[ 4 ], 10);
    if (startH > 23 || endH > 23 || startM > 59 || endM > 59) return null;

    const start = startH * 60 + startM;
    let end = endH * 60 + endM;
    if (end <= start) end += 12 * 60;
    return { start, end };
}

function timeRangesOverlap(a: { start: number; end: number }, b: { start: number; end: number }) {
    return a.start < b.end && b.start < a.end;
}

function formatHourLabel(hour: number) {
    const h = hour % 24;
    const period = h < 12 ? "AM" : "PM";
    const display = h % 12 === 0 ? 12 : h % 12;
    return `${display} ${period}`;
}

function entryKey(c: { classRoomId: string; day: string; periodIndex: number }) {
    return `${c.classRoomId}::${c.day}::${c.periodIndex}`;
}

type TimedEntry = MyTimetableEntry & { start: number; end: number };

// Groups mutually-overlapping entries (transitively — A-B and B-C overlapping
// puts all three in one group even if A/C don't directly overlap, since they
// still occupy the same visual region) into clusters via union-find. A
// cluster of size 1 just means "no conflict"; anything bigger gets rendered
// as a single merged block listing every item, instead of separately
// positioned blocks stacking on top of each other with only the last one
// visible.
function clusterOverlapping(items: TimedEntry[]): TimedEntry[][] {
    const parent = items.map((_, i) => i);
    const find = (i: number): number => {
        while (parent[ i ] !== i) { parent[ i ] = parent[ parent[ i ] ]; i = parent[ i ]; }
        return i;
    };
    const union = (i: number, j: number) => {
        const ri = find(i), rj = find(j);
        if (ri !== rj) parent[ ri ] = rj;
    };
    for (let i = 0; i < items.length; i++) {
        for (let j = i + 1; j < items.length; j++) {
            if (items[ i ].classRoomId === items[ j ].classRoomId) continue;
            if (!timeRangesOverlap(items[ i ], items[ j ])) continue;
            union(i, j);
        }
    }
    const groups = new Map<number, TimedEntry[]>();
    items.forEach((item, i) => {
        const root = find(i);
        if (!groups.has(root)) groups.set(root, []);
        groups.get(root)!.push(item);
    });
    return [ ...groups.values() ];
}

// Stable identity for a cluster regardless of member order, so expand/
// collapse state survives re-renders (a fresh fetch rebuilds `timed` from
// scratch each time).
function clusterSignature(cluster: TimedEntry[]): string {
    return cluster.map(entryKey).sort().join("|");
}

// A single, non-conflicting class — normal document flow now (no absolute
// positioning), so its natural height is just whatever the table row needs.
function SingleClassCell({ entry }: { entry: TimedEntry }) {
    const color = colorFor(entry.classRoomId);
    return (
        <div
            className={`relative rounded-md border px-2 py-1 ${color.bg} ${color.border}`}
            title={`${entry.subject} · ${entry.className}${entry.room ? ` · ${entry.room}` : ""}`}
        >
            <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-md ${color.bar}`} />
            <p className={`text-[11px] font-bold truncate pl-1 ${color.text}`}>{entry.subject}</p>
            <p className={`text-[10px] truncate pl-1 opacity-80 ${color.text}`}>{entry.className}</p>
            {entry.room && (
                <p className={`text-[10px] truncate pl-1 flex items-center gap-1 opacity-70 ${color.text}`}>
                    <MapPin className="h-2.5 w-2.5" /> {entry.room}
                </p>
            )}
        </div>
    );
}

// Two or more classes overlapping in time — merged into one cell instead of
// stacking separately positioned blocks on top of each other (which just
// showed whichever rendered last). Collapsed by default (a short preview);
// click to expand. Being in normal document flow, expanding it just grows
// this table cell — and the whole row grows with it — so it can never
// overlap a neighboring cell. The max-height + scroll is only a safety net
// for a pathologically long clash list; in practice the row simply gets
// taller.
function ConflictClusterCell({
    cluster, expanded, onToggle,
}: { cluster: TimedEntry[]; expanded: boolean; onToggle: () => void }) {
    const collapsedPreviewCount = Math.min(cluster.length, 2);
    const visible = expanded ? cluster : cluster.slice(0, collapsedPreviewCount);
    const hiddenCount = cluster.length - visible.length;

    return (
        <button
            onClick={onToggle}
            className="w-full rounded-md border-2 border-red-400 bg-red-100 ring-2 ring-red-300 flex flex-col text-left cursor-pointer hover:bg-red-200/60 transition-colors"
        >
            <div className="flex items-center gap-1 px-1.5 pt-1 shrink-0">
                <AlertTriangle className="h-3 w-3 text-red-700 shrink-0" />
                <span className="text-[9px] font-black uppercase tracking-wide text-red-800">
                    {cluster.length} clashing classes
                </span>
                {expanded
                    ? <ChevronUp className="h-3 w-3 text-red-700 ml-auto shrink-0" />
                    : <ChevronDown className="h-3 w-3 text-red-700 ml-auto shrink-0" />}
            </div>
            <div
                className={`px-1.5 pb-1 pt-1 space-y-1 ${expanded ? "overflow-y-auto" : ""}`}
                style={expanded ? { maxHeight: 260 } : undefined}
            >
                {visible.map((c, i) => (
                    <div
                        key={i}
                        className="rounded bg-white/70 border border-red-200 px-1.5 py-1"
                        title={`${c.subject} · ${c.className} · ${c.periodLabel}${c.room ? ` · ${c.room}` : ""}`}
                    >
                        <p className="text-[10px] font-bold text-red-900 truncate">{c.subject}</p>
                        <p className="text-[9px] text-red-700 truncate">{c.className} · {c.periodLabel}</p>
                    </div>
                ))}
                {!expanded && hiddenCount > 0 && (
                    <p className="text-[9px] font-bold text-red-700 pl-0.5">
                        +{hiddenCount} more — click to expand
                    </p>
                )}
            </div>
        </button>
    );
}

export default function TeachingSchedulePage() {
    const [ classes, setClasses ] = useState<MyTimetableEntry[]>([]);
    const [ isLoading, setIsLoading ] = useState(false);
    const [ error, setError ] = useState<string | null>(null);

    // null = viewing your own schedule. Set to view a colleague's instead.
    const [ viewingUid, setViewingUid ] = useState<string | null>(null);
    const [ viewingName, setViewingName ] = useState<string | null>(null);

    // Which conflict clusters are expanded to show every clashing item —
    // collapsed by default (a short preview + "+N more"), expanded on click
    // rather than an internal scrollbar so nothing is hidden behind a
    // scroll the admin might not notice.
    const [ expandedClusters, setExpandedClusters ] = useState<Set<string>>(new Set());
    const toggleCluster = (sig: string) => {
        setExpandedClusters(prev => {
            const next = new Set(prev);
            if (next.has(sig)) next.delete(sig); else next.add(sig);
            return next;
        });
    };

    // Session-level "don't re-transfer/re-parse if nothing changed" — the
    // same clientUpdatedAt/changed contract the classroom-level
    // getTimetable already uses, just without persistent local storage
    // (offline caching here isn't needed per product decision — this is an
    // admin-context dashboard, not a mobile app used without signal). Kept
    // per viewing target so switching between colleagues doesn't compare
    // one person's updated_at against another's.
    const lastUpdatedAtRef = useRef(0);

    const fetchSchedule = useCallback(async (targetUid: string | null) => {
        setIsLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ clientUpdatedAt: String(lastUpdatedAtRef.current) });
            const endpoint = targetUid
                ? `/classrooms/getFacultyTimetable?facultyUid=${encodeURIComponent(targetUid)}&${params}`
                : `/classrooms/getMyTimetable?${params}`;

            const res = await fetch(`${environment.BASE_URL}${endpoint}`, { headers: apiHeaders() });
            const data = await res.json();

            if (!data.success) {
                setError(data.message || "Failed to load teaching schedule");
                return;
            }
            if (data.changed === false) return; // nothing new — keep what's already shown

            setClasses(data.classes || []);
            lastUpdatedAtRef.current = data.updated_at || 0;
            if (targetUid && data.facultyName) setViewingName(data.facultyName);
        } catch (e: any) {
            setError(e.message);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Refetch whenever the viewing target changes — reset the change-
    // detection cursor first since it's meaningless across two different
    // people's schedules.
    useEffect(() => {
        lastUpdatedAtRef.current = 0;
        setClasses([]);
        fetchSchedule(viewingUid);
    }, [ viewingUid, fetchSchedule ]);

    const viewSelf = () => {
        setViewingUid(null);
        setViewingName(null);
    };

    const viewColleague = (t: TeacherOption) => {
        setViewingUid(t.uid);
        setViewingName(t.name);
    };

    const { timed, untimed, windowStartHour, windowEndHour, conflictKeys } = useMemo(() => {
        const timed: Array<MyTimetableEntry & { start: number; end: number }> = [];
        const untimed: MyTimetableEntry[] = [];

        for (const c of classes) {
            const parsed = parsePeriodLabel(c.periodLabel);
            if (parsed) timed.push({ ...c, ...parsed });
            else untimed.push(c);
        }

        // Same person, same day, overlapping time, two different
        // classrooms — a genuine double-booking. Both entries in each
        // colliding pair get flagged.
        const conflictKeys = new Set<string>();
        for (let i = 0; i < timed.length; i++) {
            for (let j = i + 1; j < timed.length; j++) {
                const a = timed[ i ], b = timed[ j ];
                if (a.day !== b.day) continue;
                if (a.classRoomId === b.classRoomId) continue;
                if (!timeRangesOverlap(a, b)) continue;
                conflictKeys.add(entryKey(a));
                conflictKeys.add(entryKey(b));
            }
        }

        let minHour = DEFAULT_WINDOW_START_HOUR;
        let maxHour = DEFAULT_WINDOW_END_HOUR;
        if (timed.length > 0) {
            minHour = Math.min(DEFAULT_WINDOW_START_HOUR, ...timed.map(t => Math.floor(t.start / 60)));
            maxHour = Math.max(DEFAULT_WINDOW_END_HOUR, ...timed.map(t => Math.ceil(t.end / 60)));
        }

        return { timed, untimed, windowStartHour: minHour, windowEndHour: maxHour, conflictKeys };
    }, [ classes ]);

    const hours = [];
    for (let h = windowStartHour; h < windowEndHour; h++) hours.push(h);
    const conflictCount = conflictKeys.size / 2; // each colliding pair adds 2 keys

    // Bucket each day's clusters into the table row matching the earliest
    // member's start hour — clustering itself still runs on real
    // continuous-time overlap (unchanged), only the display row is
    // hour-bucketed. cellsByDayHour[day][hour] can hold more than one
    // cluster if two unrelated (non-overlapping) classes both happen to
    // start within the same hour.
    const cellsByDayHour = new Map<string, Map<number, TimedEntry[][]>>();
    for (const day of DAY_ORDER) {
        const dayMap = new Map<number, TimedEntry[][]>();
        for (const cluster of clusterOverlapping(timed.filter(c => c.day === day))) {
            const startHour = Math.floor(Math.min(...cluster.map(c => c.start)) / 60);
            if (!dayMap.has(startHour)) dayMap.set(startHour, []);
            dayMap.get(startHour)!.push(cluster);
        }
        cellsByDayHour.set(day, dayMap);
    }

    return (
        <div className="min-h-screen bg-[oklch(0.97_0.01_90)] p-6 md:p-8 text-foreground font-sans">
            <div className="mb-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                        <CalendarClock className="h-6 w-6 text-primary" /> Teaching Schedule
                    </h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        {viewingUid
                            ? <>Viewing <span className="font-semibold text-foreground">{viewingName}</span>'s schedule</>
                            : "Every class you're assigned as subject teacher, across all classrooms"}
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {viewingUid && (
                        <Button variant="outline" size="sm" className="gap-1.5 h-9" onClick={viewSelf}>
                            <X className="h-3.5 w-3.5" /> Back to my schedule
                        </Button>
                    )}
                    <div className="w-64">
                        <TeacherCombobox
                            value=""
                            placeholder="View a colleague's schedule…"
                            onSelect={viewColleague}
                        />
                    </div>
                    <Button variant="outline" className="gap-2" onClick={() => fetchSchedule(viewingUid)} disabled={isLoading}>
                        {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                        Refresh
                    </Button>
                </div>
            </div>

            {conflictCount > 0 && (
                <div className="mb-4 flex items-center gap-2 px-4 py-3 rounded-lg border border-red-300 bg-red-50 text-red-700">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    <p className="text-xs font-semibold">
                        {conflictCount} scheduling conflict{conflictCount === 1 ? "" : "s"} found — {viewingUid ? viewingName : "this faculty member"} is
                        assigned to two different classrooms at overlapping times (highlighted below). Please contact administration to resolve.
                    </p>
                </div>
            )}

            {error ? (
                <div className="flex flex-col items-center justify-center h-64 gap-2">
                    <AlertCircle className="h-8 w-8 text-red-400" />
                    <p className="text-sm text-muted-foreground">{error}</p>
                    <Button variant="outline" size="sm" onClick={() => fetchSchedule(viewingUid)}>Retry</Button>
                </div>
            ) : isLoading && classes.length === 0 ? (
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
            ) : classes.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 gap-2">
                    <CalendarClock className="h-10 w-10 text-muted-foreground/20" />
                    <p className="text-sm text-muted-foreground">
                        {viewingUid ? `${viewingName} has no classes assigned yet` : "No classes assigned to you yet"}
                    </p>
                </div>
            ) : (
                <div className="rounded-xl border border-border/60 bg-card shadow-sm">
                    {untimed.length > 0 && (
                        <div className="border-b border-border/40 bg-muted/5 p-3 rounded-t-xl">
                            <p className="text-[9px] font-black uppercase tracking-widest text-muted-foreground mb-2">
                                Unscheduled time (period label isn't a recognizable time range)
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {untimed.map((c, i) => {
                                    const color = colorFor(c.classRoomId);
                                    return (
                                        <div
                                            key={`untimed-${c.classRoomId}-${c.periodIndex}-${i}`}
                                            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-[11px] font-semibold ${color.bg} ${color.border} ${color.text}`}
                                        >
                                            <span>{c.subject}</span>
                                            <span className="opacity-60">· {c.day.substring(0, 3)} · {c.periodLabel}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="overflow-x-auto">
                        {/* Fills the card's full width — the hour column stays a
                            fixed comfortable width, the 7 day columns split
                            whatever's left evenly. minWidth is a floor so columns
                            never squeeze illegibly narrow; below that this
                            scrolls horizontally instead. */}
                        <table
                            className="border-collapse"
                            style={{
                                tableLayout: "fixed",
                                width: "100%",
                                minWidth: HOUR_COLUMN_WIDTH + DAY_ORDER.length * DAY_COLUMN_WIDTH,
                            }}
                        >
                            <colgroup>
                                <col style={{ width: HOUR_COLUMN_WIDTH }} />
                                {DAY_ORDER.map(day => (
                                    <col key={day} style={{ width: `calc((100% - ${HOUR_COLUMN_WIDTH}px) / ${DAY_ORDER.length})` }} />
                                ))}
                            </colgroup>
                            <thead>
                                <tr className="sticky top-0 z-10 bg-muted/10">
                                    <th className="border-b border-border/40" />
                                    {DAY_ORDER.map(day => (
                                        <th key={day} className="py-2 text-center border-b border-l border-border/30">
                                            <span className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">
                                                {DAY_SHORT[ day ]}
                                            </span>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {hours.map(h => (
                                    <tr key={h}>
                                        <td className="align-top text-right pr-2 pt-1 border-t border-border/15">
                                            <span className="text-[10px] font-bold text-muted-foreground/60">
                                                {formatHourLabel(h)}
                                            </span>
                                        </td>
                                        {DAY_ORDER.map(day => {
                                            const clusters = cellsByDayHour.get(day)?.get(h) || [];
                                            return (
                                                <td
                                                    key={day}
                                                    className="align-top border-t border-l border-border/15 p-1"
                                                >
                                                    {/* Uniform floor height so empty/light rows still read as
                                                        a proper grid instead of collapsing to a sliver, and a
                                                        cap + scroll so a cell with a lot going on grows the row
                                                        by a bounded, predictable amount rather than unbounded. */}
                                                    <div className="flex flex-col gap-1 min-h-[64px] max-h-[280px] overflow-y-auto">
                                                        {clusters.map((cluster, ci) =>
                                                            cluster.length === 1
                                                                ? <SingleClassCell key={ci} entry={cluster[ 0 ]} />
                                                                : (
                                                                    <ConflictClusterCell
                                                                        key={ci}
                                                                        cluster={cluster}
                                                                        expanded={expandedClusters.has(clusterSignature(cluster))}
                                                                        onToggle={() => toggleCluster(clusterSignature(cluster))}
                                                                    />
                                                                )
                                                        )}
                                                    </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
